"use client"

import { useContext, useState, useEffect } from "react"
import {
  TextField,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Grid,
  InputAdornment,
  Divider,
  alpha,
  useTheme,
  Alert,
  Fade,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material"
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Store as StoreIcon,
} from "@mui/icons-material"
import axios from "axios"
import { AppContext } from "../../contexts/AppContext"
import { useMutation, useQueryClient } from "@tanstack/react-query"

function UserEditor({ data, storeId, onChange, onCancel }) {
  const { state } = useContext(AppContext)
  const theme = useTheme()
  const isUpdating = data?.id > 0

  const [name, setName] = useState(data?.name || "")
  const [email, setEmail] = useState(data?.email || "")
  const [phone, setPhone] = useState(data?.phone || "")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [validationError, setValidationError] = useState(null)

  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (userData) => {
      const url = isUpdating ? `/api/admin/users/${data.id}` : `/api/admin/users`
      const method = isUpdating ? "put" : "post"

      const response = await axios({
        method,
        url,
        data: userData,
        headers: {
          Authorization: state.token ? `Bearer ${state.token}` : "",
        },
      })

      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["users"])
      if (onChange) onChange()
    },
    onError: (error) => {
      console.error("Error saving user:", error)
      setValidationError("Failed to save user. Please try again.")
    },
  })

  useEffect(() => {
    setValidationError(null)
  }, [name, email, phone, password])

  const handleSubmit = () => {
    if (!name || !email || !phone || (!isUpdating && !password)) {
      setValidationError("All fields are required.")
      return
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setValidationError("Please enter a valid email address.")
      return
    }

    const userData = {
      name,
      email,
      phone,
      password: isUpdating ? undefined : password, // Don't send password if updating
      store_id: Number(storeId),
      isApproved: true,
    }

    console.log("Submitting user data:", userData)
    mutation.mutate(userData)
  }

  const toggleShowPassword = () => {
    setShowPassword(!showPassword)
  }

  // Generate initials for avatar
  const getInitials = (name) => {
    if (!name) return "U"
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2)
  }

  // Generate a consistent color based on name
  const getAvatarColor = (name) => {
    if (!name) return theme.palette.primary.main
    const colors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      "#FF6B6B",
      "#4ECDC4",
      "#FFD166",
      "#845EC2",
    ]
    const charCode = name.charCodeAt(0)
    return colors[charCode % colors.length]
  }

  return (
    <Fade in={true} timeout={300}>
      <Card
        elevation={3}
        sx={{
          maxWidth: 600,
          mx: "auto",
          mt: 3,
          borderRadius: 2,
          overflow: "visible",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        }}
      >
        <CardHeader
          avatar={
            <Avatar
              sx={{
                bgcolor: getAvatarColor(name),
                width: 48,
                height: 48,
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              }}
            >
              {getInitials(name)}
            </Avatar>
          }
          title={
            <Typography variant="h5" fontWeight="bold">
              {isUpdating ? "Edit User Account" : "Create New User"}
            </Typography>
          }
          subheader={
            <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
              <StoreIcon fontSize="small" sx={{ mr: 0.5, color: theme.palette.text.secondary }} />
              <Typography variant="body2" color="text.secondary">
                {isUpdating ? "Update user information" : "Add a new user to the system"}
              </Typography>
            </Box>
          }
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderBottom: "1px solid",
            borderColor: "divider",
            pb: 2,
          }}
        />

        <CardContent sx={{ p: 3 }}>
          {validationError && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setValidationError(null)}>
              {validationError}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                variant="outlined"
                placeholder="Enter user's full name"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                variant="outlined"
                placeholder="user@example.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                variant="outlined"
                placeholder="+1234567890"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {!isUpdating && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }}>
                  <Chip label="Security" size="small" icon={<LockIcon fontSize="small" />} variant="outlined" />
                </Divider>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mt: 2,
                    bgcolor: alpha(theme.palette.warning.main, 0.05),
                    border: "1px solid",
                    borderColor: alpha(theme.palette.warning.main, 0.2),
                    borderRadius: 1.5,
                  }}
                >
                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    variant="outlined"
                    placeholder="Create a secure password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton aria-label="toggle password visibility" onClick={toggleShowPassword} edge="end">
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    Password should be at least 8 characters long and include letters, numbers, and special characters.
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </CardContent>

        <Divider />

        <CardActions
          sx={{
            justifyContent: "space-between",
            p: 3,
            bgcolor: alpha(theme.palette.background.default, 0.5),
          }}
        >
          <Tooltip title="Cancel and discard changes">
            <Button
              variant="outlined"
              onClick={onCancel}
              color="error"
              startIcon={<CancelIcon />}
              sx={{ borderRadius: 1.5 }}
            >
              Cancel
            </Button>
          </Tooltip>

          <Tooltip title={isUpdating ? "Save changes to user" : "Create new user account"}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              color="primary"
              startIcon={<SaveIcon />}
              disabled={mutation.isPending}
              sx={{
                borderRadius: 1.5,
                px: 3,
                boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
                "&:hover": {
                  boxShadow: "0 6px 15px rgba(0, 0, 0, 0.15)",
                },
              }}
            >
              {mutation.isPending
                ? isUpdating
                  ? "Updating..."
                  : "Creating..."
                : isUpdating
                  ? "Update User"
                  : "Create User"}
            </Button>
          </Tooltip>
        </CardActions>
      </Card>
    </Fade>
  )
}

export default UserEditor

